import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Col} from "reactstrap";
import {Paper} from "@mui/material";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import Button from "../../../../../components/base/button/button";
import {Tab, Tabs} from "../../../../../components/base/Tabs";
import {Delete, Edit, Window} from "@mui/icons-material";
import IconButton from "../../../../../components/base/button/icon_button";
import ConnectToPrinterHub from "../../../../../components/app-specific/connect-to-printer-hub";
import ConnectToPrinterHubDialog from "../../../../../components/dialogs/connect-to-printer-hub";
import EditPrinterHubDialog from "../../../../../components/dialogs/edit-printer-hub";
import Api from "../../../../../../core/services/api/api";
import DownloadPrinterHubAppDialog from "../../../../../components/dialogs/download-printer-hub-app";
import PrintHubViewHubInfo from "./sections/hub-info";
import PrintHubQueuesList from "./sections/queues-list";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";


const initialEditHubDialogState = {
    open: false,
    hub: null,
}

const initialNewHubDialogState = {
    open: false,
}

const PrintHubView = () => {
    const [hubs, setHubs] = useState([]);
    const [loadingHubs, setLoadingHubs] = useState(true);
    const [selectedHubId, setSelectedHubId] = useState(null);
    const [editHubDialogState, setEditHubDialogState] = useState(initialEditHubDialogState);
    const [newHubDialogState, setNewHubDialogState] = useState(initialNewHubDialogState);
    const [downloadAppDialogState, setDownloadAppDialogState] = useState(false);
    const isMounted = useIsMounted();

    const printHubQueuesListRef = useRef(null);
    const printHubInfoRef = useRef(null);

    const selectedHub = useMemo(() => hubs.find(hub => hub.id === selectedHubId), [hubs, selectedHubId]);

    const {configureNotebookTags} = useConfigureNotebook('print-hub-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.printHub);
        configureBehavioralTool((tags) => tags.printHub);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Fetches the list of printer Hubs from the server.
     * @type {function(): Promise<void>}
     */
    const getHubs = useCallback(async () => {
        setLoadingHubs(true);
        const response = await Api.getPrinterHubs();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setHubs(response.data ?? []);
            setSelectedHubId(prevState => {
                if (!prevState || !response.data.find(e => e.id === prevState))
                    return response.data[0]?.id ?? null;
                return prevState;
            });
        }
        setLoadingHubs(false);
    }, [isMounted]);

    /**
     * As soon as the component is mounted:
     * - gets the list of hubs.
     */
    useEffect(() => {
        getHubs().then();
    }, [getHubs])

    /**
     * Adds a new hub to the list of hubs and selects it.
     * @type {(function(*): void)}
     */
    const onNewHubConnected = useCallback((hub) => {
        setHubs([...hubs, {...hub, printers: hub.printers ?? [],}]);
        setSelectedHubId(hub.id);
    }, [hubs]);


    /**
     * Selects the tab with the given id.
     * @type {(function(*,number): void)}
     */
    const onTabChanged = useCallback((e, tabId) => {
        setSelectedHubId(tabId);
    }, [])

    /**
     * Opens the edit hub dialog with the given hub.
     * @type {(function(*, *): void)}
     */
    const onEditHubName = useCallback((e, hub) => {
        e.stopPropagation();
        e.preventDefault();
        setEditHubDialogState({
            open: true,
            hub,
        });
    }, []);

    /**
     * Edits the given hub in the list of hubs.
     * @type {(function(*): void)}
     */
    const editPrintHub = useCallback((hub) => {
        setHubs(hubs => hubs.map(h => h.id === hub.id ? {...h, ...hub} : h));
    }, []);

    /**
     * Opens the download app dialog.
     * @type {(function())}
     */
    const openDownloadAppDialog = useCallback(() => {
        setDownloadAppDialogState(true);
    }, []);

    /**
     * Gets users confirmation before removing the current note entirely.
     */
    const getRemoveConfirmation = (e, selectedHub) => {
        e.stopPropagation();
        e.preventDefault();
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Are you sure that you want to remove this Hub?
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && removeSelectedHub(selectedHub),
        );
    }

    /**
     * Removes the selected hub from the list of hubs and selects the previous one.
     * @type {(function(*): Promise<void>)}
     */
    const removeSelectedHub = useCallback(async (selectedHub) => {
        setHubs(prevState => prevState.map(hub => hub.id === selectedHub.id ? {...hub, removing: true} : hub));
        const response = await Api.disconnectPrinterHub(selectedHub.id);
        if (!isMounted())
            return;
        setHubs(prevState => prevState.map(hub => hub.id === selectedHub.id ? {...hub, removing: false} : hub));
        if (response?.resultFlag) {
            const index = hubs.findIndex(hub => hub.id === selectedHub.id);
            if (index === -1)
                return;
            if (hubs.length === 1) {
                setSelectedHubId(null);
            } else {
                setSelectedHubId(index === 0 ? hubs[1]?.id : hubs[index - 1]?.id);
            }
            setHubs(hubs => hubs.filter(hub => hub.id !== selectedHub.id));
        }
    }, [hubs, isMounted]);

    /**
     * Sets the print config from the given history.
     * @type {(function(*): void)}
     */
    const onHistorySelected = useCallback((queue) => {
        printHubInfoRef.current?.setConfigFromQueue?.(queue);
    }, []);

    /**
     * Adds the given queue to the list of queues.
     * @type {(function(*): void)}
     */
    const onPDFPrinted = useCallback((queue) => {
        printHubQueuesListRef.current?.addQueue?.(queue);
    }, [printHubQueuesListRef]);

    /**
     * Sets the printer list of the selected hub.
     * @type {(function(*): void)}
     */
    const setPrinterList = useCallback((printerList) => {
        setHubs(prevState => prevState.map(hub => hub.id === selectedHubId ? {...hub, printers: printerList} : hub));
    }, [selectedHubId]);

    return (
        <>
            <div className={'print-hub-view'}>
                <div className={'d-flex align-items-center justify-content-between'}>
                    <p className={'view-title'}>
                        Print Hub
                    </p>
                    {
                        !loadingHubs &&
                        <div className={'d-flex align-items-center justify-content-end'}>
                            {
                                !!hubs.length &&
                                <Button
                                    variant={'outlined'}
                                    color={'primary'}
                                    onClick={() => setNewHubDialogState({open: true})}
                                >
                                    Add a new Printer Hub
                                </Button>
                            }
                            <Button
                                variant={'outlined'}
                                color={'primary'}
                                className={'ml-2 mr-3 d-flex align-items-center justify-content-center'}
                                onClick={openDownloadAppDialog}
                            >
                                <p>Install Windows App</p>
                                <Window className={'ml-2'}/>
                            </Button>
                        </div>
                    }
                </div>
                {
                    loadingHubs &&
                    <div className='loading compare-item w-100 d-flex flex-wrap'>
                        <Col xs={12} className='pb-3'>
                            <div/>
                        </Col>
                    </div>
                }
                {
                    !loadingHubs &&
                    <>
                        {
                            !hubs.length &&
                            <Paper className='paper w-100 p-4 mt-3'>
                                <Col xs={12} lg={6} className={'m-auto'}>
                                    <ConnectToPrinterHub
                                        onConnect={onNewHubConnected}
                                        withButton
                                    />
                                </Col>

                            </Paper>
                        }
                        {
                            !!hubs.length &&
                            <>
                                <div className='w-100 flex-grow-1'>
                                    <Tabs value={selectedHubId ?? false} onChange={onTabChanged}>
                                        {
                                            hubs.map(hub => (
                                                <Tab
                                                    key={hub.id}
                                                    value={hub.id}
                                                    className={'only-on-hover-container'}
                                                    label={
                                                        <div className={'d-flex align-items-center justify-content-center'}>
                                                            <p>{hub.name ?? ''}</p>
                                                            <IconButton
                                                                disabled={hub.removing}
                                                                className={"mx-1 ml-2 only-on-hover"}
                                                                color={'secondary'}
                                                                size={'small'}
                                                                component={'span'}
                                                                onPressed={(e) => onEditHubName(e, hub)}
                                                            >
                                                                <Edit className={'icon small'}/>
                                                            </IconButton>
                                                            <IconButton
                                                                disabled={hub.removing}
                                                                className={"mx-1 only-on-hover"}
                                                                color={'paper.error'}
                                                                size={'small'}
                                                                component={'span'}
                                                                onPressed={(e) => getRemoveConfirmation(e, hub)}
                                                            >
                                                                <Delete className={'icon small'}/>
                                                            </IconButton>
                                                        </div>
                                                    }
                                                />
                                            ))
                                        }
                                    </Tabs>
                                </div>
                                {
                                    !!selectedHub &&
                                    <Paper className='paper w-100 p-4 mt-3 d-flex flex-wrap align-items-start'>
                                        <Col xs={12} lg={6}>
                                            <PrintHubViewHubInfo
                                                selectedHub={selectedHub}
                                                ref={printHubInfoRef}
                                                onPDFPrinted={onPDFPrinted}
                                                setPrinterList={setPrinterList}
                                            />
                                        </Col>
                                        <Col xs={12} lg={6}>
                                            <PrintHubQueuesList
                                                hubId={selectedHubId}
                                                ref={printHubQueuesListRef}
                                                onHistorySelected={onHistorySelected}
                                            />
                                        </Col>
                                    </Paper>
                                }
                            </>
                        }
                    </>
                }
            </div>
            <ConnectToPrinterHubDialog
                open={newHubDialogState.open}
                onClose={() => setNewHubDialogState(initialNewHubDialogState)}
                onConnect={onNewHubConnected}
            />
            <EditPrinterHubDialog
                open={editHubDialogState.open}
                hub={editHubDialogState.hub}
                onClose={() => setEditHubDialogState(initialEditHubDialogState)}
                onEdit={editPrintHub}
            />
            <DownloadPrinterHubAppDialog
                open={downloadAppDialogState}
                onClose={() => setDownloadAppDialogState(false)}
            />
        </>
    )
}

export default PrintHubView;
